import type { PropsWithChildren, ReactElement, ReactNode } from 'react';
import { isElement } from 'react-is';

import type { DataTableColumnDef, DataTableRowData } from '../../public.api.js';

/**
 * Props for the `DataTableColumnActions` component.
 * @public
 */
export interface DataTableColumnActionsProps<TData extends DataTableRowData> {
  /**
   * Content for the column actions.
   * Should be a `TableActionsMenu` directly or a function returning one.
   */
  children: DataTableColumnActionsContent<TData>;
  /**
   * Column id that the given column actions apply to. If left empty, the given column actions
   * apply to all columns that allow column actions.
   */
  column?: string;
}

/**
 * Type for the content of a DataTableColumnAction.
 * @public
 */
export type DataTableColumnActionsContent<TData extends DataTableRowData> =
  | ReactNode
  | ((column: DataTableColumnDef<TData>) => ReactElement<PropsWithChildren>);

/**
 * Type for the column actions collected from the table configuration.
 * `column` is the extracted column prop of the ColumnActions slot component
 * and `action` is the content.
 * @internal
 */
export type CollectedColumnActions<TData> = Array<{
  column?: string;
  action: DataTableColumnActionsContent<TData>;
}>;

/**
 * The slot component for defining column actions within the DataTable.
 * @public
 */
export function DataTableColumnActions<TData extends DataTableRowData>(
  props: DataTableColumnActionsProps<TData>,
) {
  return null;
}

/**
 * Typeguard function that will check if a ReactNode is of type DataTableColumnActions.
 * @internal
 */
export function isDataTableColumnActions<TData extends DataTableRowData>(
  child: ReactNode,
): child is ReactElement<DataTableColumnActionsProps<TData>> {
  if (isElement(child) && child.type === DataTableColumnActions) {
    return true;
  }
  return false;
}
